import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { CloseButton, ToolTip } from '@repo/common';
import { cn } from '../model/derive';
import usePresence from '../usePresence';

/** Complete project 공통 UI (Card · Alert · Sheet · AlertDialog · Tooltip) */

export const btn = {
  base: 'inline-flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-full text-[14px] font-medium transition-colors',
  secondary: 'bg-green-deep03/[0.08] px-[18px] text-text-primary hover:bg-green-deep03/[0.14]',
  primary:
    'bg-green-deep02 px-[22px] text-grayscale-gray00 hover:bg-green-deep04 active:scale-[0.97]',
  disabled: 'cursor-not-allowed bg-grayscale-gray02 px-[22px] text-text-placeholder',
};

export const Card = ({ className, children, ...rest }) => (
  <section
    className={cn(
      'flex flex-col gap-4 rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 p-4 shadow-4 md:px-6 md:py-5',
      className,
    )}
    {...rest}
  >
    {children}
  </section>
);

export const CardTitle = ({ children }) => (
  <h2 className='text-[15px] font-medium leading-[140%] text-text-primary'>{children}</h2>
);

export const Alert = ({ title, children, role = 'note' }) => (
  <div
    role={role}
    className='flex gap-2.5 rounded-xl border border-green-deep03/[0.12] bg-surface-green10 px-4 py-3'
  >
    <span aria-hidden className='mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-green-deep02' />
    <div className='flex flex-col gap-0.5'>
      {title && (
        <span className='text-[14px] font-medium leading-[150%] text-text-primary'>{title}</span>
      )}
      <span className='text-[13px] leading-[150%] text-text-secondary [text-wrap:pretty]'>
        {children}
      </span>
    </div>
  </div>
);

export const LeaderBadge = ({ large }) => (
  <span
    className={cn(
      'inline-flex items-center rounded-md bg-surface-green10 font-medium text-green-deep04',
      large ? 'h-[22px] px-2 text-[12px]' : 'h-5 px-[7px] text-[11px]',
    )}
  >
    Leader
  </span>
);

export const Footer = ({ children }) => (
  <div className='sticky bottom-0 z-[2] -mx-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2.5 border-t border-green-deep03/[0.08] bg-grayscale-gray00 px-4 pb-5 pt-3 md:static md:mx-0 md:border-t-0 md:bg-transparent md:p-0'>
    {children}
  </div>
);

const useOverlay = (mounted, onClose) => {
  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [mounted, onClose]);
};

export const Sheet = ({ open, onClose, title, subtitle, children }) => {
  const { mounted, shown } = usePresence(open);
  const closeRef = useRef(null);
  useOverlay(mounted, onClose);
  useEffect(() => {
    if (shown) closeRef.current?.querySelector('button')?.focus();
  }, [shown]);
  if (!mounted) return null;
  return ReactDOM.createPortal(
    <div
      onMouseDown={onClose}
      className={cn(
        'fixed inset-0 z-[1000] flex items-end justify-end bg-grayscale-gray09/40 transition-opacity duration-300 motion-reduce:transition-none md:items-stretch',
        shown ? 'opacity-100' : 'opacity-0',
      )}
    >
      <aside
        role='dialog'
        aria-modal='true'
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        className={cn(
          'flex h-[88%] w-full flex-col rounded-t-3xl bg-grayscale-gray00 shadow-popup-01 transition-transform duration-300 ease-out motion-reduce:transition-none md:h-full md:w-[440px] md:rounded-none',
          shown ? 'translate-x-0 translate-y-0' : 'max-md:translate-y-full md:translate-x-full',
        )}
      >
        <div className='flex items-start justify-between gap-3 px-5 pb-4 pt-5 md:px-6 md:pt-6'>
          <div className='flex flex-col gap-1'>
            <h2 className='text-[18px] font-medium leading-[140%] text-text-primary'>{title}</h2>
            {subtitle && (
              <span className='text-[13px] leading-[140%] text-text-secondary'>{subtitle}</span>
            )}
          </div>
          {/* CloseButton은 ref를 전달하지 않아 래퍼에서 내부 버튼으로 포커스 */}
          <span ref={closeRef} className='flex shrink-0'>
            <CloseButton onClick={onClose} />
          </span>
        </div>
        <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 pb-5 md:px-6 md:pb-6'>
          {children}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export const AlertDialog = ({ open, onCancel, busy, title, description, children, actions }) => {
  const { mounted, shown } = usePresence(open);
  const close = busy ? () => {} : onCancel;
  useOverlay(mounted, close);
  if (!mounted) return null;
  return ReactDOM.createPortal(
    <div
      onMouseDown={close}
      className={cn(
        'fixed inset-0 z-[1000] flex items-end justify-center bg-grayscale-gray09/40 transition-opacity duration-300 motion-reduce:transition-none md:items-center md:p-4',
        shown ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div
        role='alertdialog'
        aria-modal='true'
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
        className={cn(
          'flex w-full max-w-[440px] flex-col gap-5 rounded-t-3xl bg-grayscale-gray00 px-5 pb-7 pt-6 shadow-popup-01 transition-[transform,opacity] duration-300 ease-out motion-reduce:transition-none md:rounded-[20px] md:p-7',
          shown
            ? 'translate-y-0 md:scale-100 md:opacity-100'
            : 'max-md:translate-y-full md:scale-[0.97] md:opacity-0',
        )}
      >
        <div className='flex flex-col gap-2'>
          <h2 className='text-[18px] font-medium leading-[140%] text-text-primary'>{title}</h2>
          {description && (
            <p className='text-[14px] leading-[150%] text-text-secondary'>{description}</p>
          )}
        </div>
        {children}
        <div className='flex flex-wrap justify-end gap-2 [&>*]:flex-1 md:[&>*]:flex-none'>
          {actions}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const Tooltip = ({ show, children }) =>
  show ? (
    <span role='tooltip' className='pointer-events-none absolute inset-x-2 top-[calc(100%+10px)] z-30'>
      <ToolTip
        position='top'
        state='accent'
        line='multi'
        label={children}
        className='block'
        containerClass='[text-wrap:pretty]'
      />
    </span>
  ) : null;
